import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Slider,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { TechDocService } from '../services/techDocService';
import { 
  TechDocSession, 
  TechDocSessionStorage, 
  MultiTechDocRequest,
  DOC_TYPES,
  TECHNICAL_LEVELS,
  COMMON_SECTIONS
} from '../models/techDoc';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`techdoc-tabpanel-${index}`}
      aria-labelledby={`techdoc-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  );
}

const TechDocMultiAgent: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [tabValue, setTabValue] = useState(0);
  const [subject, setSubject] = useState('');
  const [docType, setDocType] = useState('');
  const [technicalLevel, setTechnicalLevel] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [keySections, setKeySections] = useState<string[]>([]);
  const [includeExamples, setIncludeExamples] = useState(true);
  const [maxTokens, setMaxTokens] = useState(3072);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [finalDocument, setFinalDocument] = useState('');
  const [sessions, setSessions] = useState<TechDocSession[]>([]);

  // Load sessions from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('techDocSessions');
    if (stored) {
      const data: TechDocSessionStorage = JSON.parse(stored);
      setSessions(data.sessions.map(s => ({
        ...s,
        createdAt: new Date(s.createdAt)
      })));
    }
  }, []);

  // Save sessions to localStorage
  const saveSessions = (newSessions: TechDocSession[]) => {
    const storage: TechDocSessionStorage = {
      sessions: newSessions,
      lastUpdated: new Date()
    };
    localStorage.setItem('techDocSessions', JSON.stringify(storage));
    setSessions(newSessions);
  };

  const handleGenerate = async () => {
    if (!subject.trim()) {
      setError('Please enter a documentation subject');
      return;
    }

    setLoading(true);
    setError(null);
    setFinalDocument('');

    try {
      const request: MultiTechDocRequest = {
        subject: subject.trim(),
        docType: docType || undefined,
        technicalLevel: technicalLevel || undefined,
        targetAudience: targetAudience.trim() || undefined,
        keySections: keySections.length > 0 ? keySections : undefined,
        includeExamples,
        maxTokensPerAgentTurn: maxTokens
      };

      const response = await TechDocService.generateTechDoc(request);
      
      if (response.finalDocument) {
        setFinalDocument(response.finalDocument);
        
        // Save to sessions
        const newSession: TechDocSession = {
          id: Date.now().toString(),
          subject: subject.trim(),
          docType: docType || undefined,
          technicalLevel: technicalLevel || undefined,
          targetAudience: targetAudience.trim() || undefined,
          keySections: keySections.length > 0 ? keySections : undefined,
          finalDocument: response.finalDocument,
          createdAt: new Date(),
          includeExamples
        };
        
        saveSessions([newSession, ...sessions]);
        setTabValue(1); // Switch to results tab
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate documentation');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleDownload = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleLoadSession = (session: TechDocSession) => {
    setSubject(session.subject);
    setDocType(session.docType || '');
    setTechnicalLevel(session.technicalLevel || '');
    setTargetAudience(session.targetAudience || '');
    setKeySections(session.keySections || []);
    setFinalDocument(session.finalDocument || '');
    setIncludeExamples(session.includeExamples ?? true);
    setTabValue(1); // Switch to results tab
  };

  const handleDeleteSession = (sessionId: string) => {
    saveSessions(sessions.filter(s => s.id !== sessionId));
  };

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 2, sm: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 },
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <CodeIcon sx={{ 
            mr: { xs: 1, sm: 2 }, 
            fontSize: { xs: 32, sm: 40 }, 
            color: 'primary.main' 
          }} />
          <Box sx={{ flex: 1, minWidth: isMobile ? '100%' : 0, mt: isMobile ? 1 : 0 }}>
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ fontSize: { xs: '1.5rem', sm: '1.875rem', md: '2.125rem' } }}
            >
              {isMobile ? 'Tech Doc Agent' : 'Tech Documentation Multi-Agent'}
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {isMobile ? 'Generate tech docs with AI' : 'Generate comprehensive technical documentation using multiple specialized AI agents'}
            </Typography>
          </Box>
        </Box>

        <Tabs 
          value={tabValue} 
          onChange={(_, newValue) => setTabValue(newValue)}
          variant={isMobile ? 'fullWidth' : 'standard'}
        >
          <Tab 
            label="Generate" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="Results" 
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
          <Tab 
            label="History" 
            icon={<HistoryIcon />}
            sx={{ 
              minHeight: 48,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
            <TextField
              fullWidth
              label={isMobile ? 'Subject' : 'Documentation Subject'}
              placeholder={isMobile ? 'e.g., React Auth Library' : 'e.g., React Authentication Library, REST API for E-commerce Platform'}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              helperText={isMobile ? undefined : 'The technology, library, API, or system you want to document'}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Type' : 'Document Type'}
                </InputLabel>
                <Select
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  label={isMobile ? 'Type' : 'Document Type'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                  {DOC_TYPES.map((type) => (
                    <MenuItem 
                      key={type} 
                      value={type}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Level' : 'Technical Level'}
                </InputLabel>
                <Select
                  value={technicalLevel}
                  onChange={(e) => setTechnicalLevel(e.target.value)}
                  label={isMobile ? 'Level' : 'Technical Level'}
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  <MenuItem value="" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>None</MenuItem>
                  {TECHNICAL_LEVELS.map((level) => (
                    <MenuItem 
                      key={level} 
                      value={level}
                      sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                    >
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label={isMobile ? 'Audience' : 'Target Audience'}
              placeholder={isMobile ? 'e.g., Frontend devs' : 'e.g., Frontend developers, DevOps engineers, API consumers'}
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              helperText={isMobile ? undefined : 'Who will be reading this documentation?'}
              InputProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
            />

            <Box>
              <Typography 
                variant="subtitle1" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'Sections' : 'Key Sections to Include'}
              </Typography>
              <Autocomplete
                multiple
                options={COMMON_SECTIONS}
                value={keySections}
                onChange={(_, newValue) => setKeySections(newValue)}
                freeSolo
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={isMobile ? 'Select sections' : 'Select or type sections'}
                    helperText={isMobile ? undefined : 'Choose from suggestions or add custom sections'}
                    InputProps={{
                      ...params.InputProps,
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography 
                gutterBottom
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {isMobile ? 'Tokens' : 'Max Tokens per Agent'}: {maxTokens}
              </Typography>
              <Slider
                value={maxTokens}
                onChange={(_, value) => setMaxTokens(value as number)}
                min={1024}
                max={8192}
                step={512}
                valueLabelDisplay="auto"
                marks={[
                  { value: 1024, label: '1K' },
                  { value: 3072, label: '3K' },
                  { value: 5120, label: '5K' },
                  { value: 8192, label: '8K' },
                ]}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={includeExamples}
                  onChange={(e) => setIncludeExamples(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {isMobile ? 'Include examples' : 'Include code examples and sample implementations'}
                </Typography>
              }
            />

            {error && (
              <Alert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {error}
              </Alert>
            )}

            <Button
              variant="contained"
              size={isMobile ? 'medium' : 'large'}
              onClick={handleGenerate}
              disabled={loading || !subject.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : (!isMobile && <CodeIcon />)}
              sx={{
                minHeight: 44,
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              {loading ? (isMobile ? 'Generating...' : 'Generating Documentation...') : (isMobile ? 'Generate' : 'Generate Documentation')}
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {finalDocument ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    mb: 2,
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Typography 
                      variant="h6"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
                    >
                      {isMobile ? 'Documentation' : 'Technical Documentation'}
                    </Typography>
                    <Box>
                      <Tooltip title={isMobile ? '' : 'Copy to clipboard'}>
                        <IconButton 
                          onClick={() => handleCopy(finalDocument)} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isMobile ? '' : 'Download as Markdown'}>
                        <IconButton 
                          onClick={() => handleDownload(
                            finalDocument, 
                            `${subject.replace(/\s+/g, '-').toLowerCase()}-docs.md`
                          )} 
                          size="small"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: 'grey.50',
                      p: { xs: 2, sm: 3 },
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: { xs: '50vh', sm: '70vh' }
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: { xs: '0.8rem', sm: '0.95rem' },
                        lineHeight: 1.6,
                        m: 0
                      }}
                    >
                      {finalDocument}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <CodeIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.secondary', 
                mb: 2 
              }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No docs yet' : 'No documentation generated yet'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Go to Generate tab' : 'Go to the Generate tab to create technical documentation'}
              </Typography>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {sessions.length > 0 ? (
            <List>
              {sessions.map((session) => (
                <ListItem 
                  key={session.id} 
                  divider
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    py: { xs: 1.5, sm: 2 }
                  }}
                >
                  <ListItemText
                    primary={session.subject}
                    secondary={
                      <>
                        {session.docType && `${session.docType} • `}
                        {session.technicalLevel && `${session.technicalLevel} • `}
                        {isMobile ? (
                          session.createdAt.toLocaleDateString()
                        ) : (
                          <>
                            {session.targetAudience && `For: ${session.targetAudience} • `}
                            Created: {session.createdAt.toLocaleDateString()}
                          </>
                        )}
                      </>
                    }
                    onClick={() => handleLoadSession(session)}
                    primaryTypographyProps={{
                      sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
                    }}
                    secondaryTypographyProps={{
                      sx: { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
                    }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={isMobile ? '' : 'Delete'}>
                      <IconButton 
                        edge="end" 
                        onClick={() => handleDeleteSession(session.id)}
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <HistoryIcon sx={{ 
                fontSize: { xs: 60, sm: 80 }, 
                color: 'text.secondary', 
                mb: 2 
              }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}
              >
                {isMobile ? 'No history' : 'No documentation history'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ 
                  mt: 1,
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                {isMobile ? 'Docs will appear here' : 'Generated documentation will appear here'}
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default TechDocMultiAgent;